import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Search, Clock, CheckCircle2, XCircle, Package,
  ChefHat, AlertCircle, Filter, Calendar, Plus,
  Trash2, ExternalLink, ChevronRight, MapPin, Phone
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from '../components/ui/notify';
import 'react-toastify/dist/ReactToastify.css';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { base_url } from '../api/index';

import SelectCourierModal from "../components/SelectCourierModal";
import AddOrderModal from "../components/AddOrderModal";
import DontActiveAcount from "../components/DontActiveAcount";
import AccessDenied from "../components/AccessDenied";
import HesapKesSip from "../components/HesapKesSip";
import { PosLoader } from "../components/pos/PosLoader";
import { useI18n } from "../i18n/I18nContext";
import { useConfirm } from "../components/ConfirmDialog";

const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const statusConfig = {
  pending: {
    color: 'text-amber-600',
    labelKey: 'orders.status.pending',
    icon: AlertCircle,
    bgLight: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
  preparing: {
    color: 'text-blue-600',
    labelKey: 'orders.status.preparing',
    icon: ChefHat,
    bgLight: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  ready: {
    color: 'text-emerald-600',
    labelKey: 'orders.status.ready',
    icon: CheckCircle2,
    bgLight: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
  },
  served: {
    color: 'text-purple-600',
    labelKey: 'orders.status.served',
    icon: CheckCircle2,
    bgLight: 'bg-purple-50',
    borderColor: 'border-purple-200',
  },
  cancelled: {
    color: 'text-rose-600',
    labelKey: 'orders.status.cancelled',
    icon: XCircle,
    bgLight: 'bg-rose-50',
    borderColor: 'border-rose-200',
  },
};

export default function Siparisler() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { confirm, confirmDialog } = useConfirm();
  const [activeTab, setActiveTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modals state
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [showSelectCourier, setShowSelectCourier] = useState(false);
  const [showSettlement, setShowSettlement] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [activeUser, setActiveUser] = useState(false);

  const fetchOrderDetails = async (id) => {
    try {
      const response = await axios.get(`${base_url}/quick-orders/${id}`, getHeaders());
      return response.data;
    } catch (err: any) {
      console.error(`Error fetching details for order ${id}:`, err);
      return null;
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${base_url}/quick-orders`, getHeaders());
      const basicOrders = response.data;

      const detailedOrders = await Promise.all(
        basicOrders.map(async (order) => {
          const details = await fetchOrderDetails(order.id);
          if (!details) return order;

          return {
            ...order,
            items: details.order?.stocks?.map(s => ({
              id: s.id,
              name: s.name,
              quantity: s.pivot?.quantity || 1,
              price: s.price,
              notes: s.notes || ''
            })) || [],
            total: details.order?.total_price || 0,
            prepayment: details.order?.total_prepayment || 0,
            status: details.order?.status || 'pending',
            waiter: order.courier?.name || t('orders.waiterUnset'),
            createdAt: new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            prepTime: 15,
            customerName: order.name,
            phone: order.phone,
            address: order.address
          };
        })
      );

      setOrders(detailedOrders);
    } catch (err: any) {
      if (err.response?.status === 403) {
        if (err.response.data?.message?.includes("active restaurant")) {
          setActiveUser(true);
        } else {
          setAccessDenied(true);
        }
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleDeleteOrder = async (orderId) => {
    const ok = await confirm({
      message: t("orders.deleteConfirm"),
      confirmLabel: t("common.delete"),
      variant: "danger",
    });
    if (!ok) return;
    try {
      await axios.delete(`${base_url}/quick-orders/${orderId}`, getHeaders());
      setOrders(prev => prev.filter(o => o.id !== orderId));
      toast.success(t("orders.deleted"));
    } catch (err: any) {
      toast.error(t("orders.deleteError"));
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    toast.info(t("orders.statusUpdating", { label: t(statusConfig[newStatus].labelKey) }));
    setOrders(prev => prev.map(o =>
      o.id === orderId ? { ...o, status: newStatus } : o
    ));
  };

  const filteredOrders = orders.filter((order) => {
    const isHistory = order.status === 'served' || order.status === 'cancelled';
    const tabMatch = activeTab === 'active' ? !isHistory : isHistory;

    const matchesSearch =
      order.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.phone?.includes(searchQuery) ||
      order.items?.some(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    return tabMatch && matchesSearch && matchesStatus;
  });

  const activeStats = {
    pending: orders.filter(o => o.status === 'pending').length,
    preparing: orders.filter(o => o.status === 'preparing').length,
    ready: orders.filter(o => o.status === 'ready').length,
  };

  if (activeUser) return <DontActiveAcount sil={() => setActiveUser(false)} />;
  if (accessDenied) return <AccessDenied onClose={() => setAccessDenied(false)} />;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a]/50 dark:bg-[#0f1117] pt-4">
      <Helmet>
        <title>{t("nav.orders")} | Smartcafe</title>
      </Helmet>

      {/* Toast: App-dəki tək qlobal ToastContainer istifadə olunur. */}

      {/* Header with Tabs */}
      <div className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] border-b border-gray-100 p-6 mx-4 rounded-t-2xl shadow-sm">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-1 text-slate-800">{t("nav.orders")}</h1>
            <p className="text-slate-500">
              {activeTab === 'active'
                ? t("orders.activeCount", { n: activeStats.pending + activeStats.preparing + activeStats.ready })
                : t("orders.historyCount", { n: orders.length - (activeStats.pending + activeStats.preparing + activeStats.ready) })}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowAddOrder(true)}
              className="px-6 py-2.5 bg-[var(--primary)] hover:bg-orange-600 text-white rounded-xl font-semibold text-sm transition-all flex items-center gap-2 shadow-lg shadow-orange-200"
            >
              <Plus className="w-4 h-4" />
              {t("orders.new")}
            </button>

            {/* Tab Switcher */}
            <div className="flex gap-1 bg-slate-100 dark:bg-[#141824] dark:border-[#2a2e3a] rounded-xl p-1">
              <button
                onClick={() => setActiveTab('active')}
                className={`
                  px-6 py-2 rounded-lg font-semibold text-sm transition-all
                  ${activeTab === 'active'
                    ? 'bg-white text-[var(--primary)] shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                  }
                `}
              >
                {t("orders.tab.active")}
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`
                  px-6 py-2 rounded-lg font-semibold text-sm transition-all
                  ${activeTab === 'history'
                    ? 'bg-white text-[var(--primary)] shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                  }
                `}
              >
                {t("orders.tab.history")}
              </button>
            </div>
          </div>
        </div>

        {/* Stats for Active Orders */}
        {activeTab === 'active' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-amber-50 rounded-2xl p-4 border border-amber-100"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-600">{t("orders.status.pending")}</p>
                  <p className="text-2xl font-bold text-amber-800">{activeStats.pending}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-blue-50 rounded-2xl p-4 border border-blue-100"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <ChefHat className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-blue-600">{t("orders.status.preparing")}</p>
                  <p className="text-2xl font-bold text-blue-800">{activeStats.preparing}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-emerald-600">{t("orders.status.ready")}</p>
                  <p className="text-2xl font-bold text-emerald-800">{activeStats.ready}</p>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] border-b border-gray-100 p-4 mx-4 shadow-sm">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={t("orders.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a] border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>

          {activeTab === 'active' && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a] border border-slate-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer"
              >
                <option value="all">{t("orders.allStatuses")}</option>
                <option value="pending">{t("orders.status.pending")}</option>
                <option value="preparing">{t("orders.status.preparing")}</option>
                <option value="ready">{t("orders.status.ready")}</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Orders List */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <PosLoader size="lg" label={t("orders.loading")} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filteredOrders.map((order) => {
                const config = statusConfig[order.status] || statusConfig.pending;
                const StatusIcon = config.icon;

                return (
                  <motion.div
                    key={order.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-xl transition-all relative group"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-xl font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                            {order.customerName}
                          </h3>
                          <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${config.color} ${config.bgLight} border ${config.borderColor} flex items-center gap-1.5`}>
                            <StatusIcon className="w-3 h-3" />
                            {t(config.labelKey)}
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {order.phone}
                          </p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <ChefHat className="w-3 h-3" /> {order.waiter}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 mb-1">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{order.createdAt}</span>
                        </div>
                        {order.prepTime && (
                          <span className="text-[10px] font-bold text-[var(--primary)] bg-orange-50 px-1.5 py-0.5 rounded">
                            {t("orders.min", { n: order.prepTime })}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Items */}
                    <div className="space-y-3 mb-6 bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a] p-4 rounded-xl border border-slate-100">
                      {order.items?.map((item) => (
                        <div key={item.id} className="flex items-start justify-between text-sm">
                          <div className="flex-1">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] text-slate-900 font-bold text-xs mr-2 border border-slate-200">
                              {item.quantity}
                            </span>
                            <span className="text-slate-700 font-medium">{item.name}</span>
                            {item.notes && (
                              <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3 text-amber-500" />
                                {t("orders.note", { note: item.notes })}
                              </p>
                            )}
                          </div>
                          <span className="font-bold text-slate-900 ml-2">
                            ₼ {((item.price) * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Progress Bar (Visual Only) */}
                    {activeTab === 'active' && order.status !== 'ready' && (
                      <div className="w-full bg-slate-100 dark:bg-[#141824] dark:border-[#2a2e3a] rounded-full h-1 mb-4 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: order.status === 'pending' ? '33%' : '66%' }}
                          className={`h-full ${order.status === 'pending' ? 'bg-amber-400' : 'bg-blue-500'}`}
                        />
                      </div>
                    )}

                    {/* Total */}
                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between mb-5">
                      <span className="font-semibold text-slate-400 text-sm">{t("orders.totalAmount")}</span>
                      <span className="text-2xl font-black text-[var(--primary)]">
                        ₼ {order.total.toFixed(2)}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {activeTab === 'active' && (
                        <>
                          {order.status === 'pending' && (
                            <button
                              onClick={() => handleStatusChange(order.id, 'preparing')}
                              className="flex-1 px-4 py-3 bg-[var(--primary)] text-white rounded-xl font-bold hover:bg-orange-600 transition-all text-xs shadow-lg shadow-orange-100"
                            >
                              {t("orders.sendToKitchen")}
                            </button>
                          )}
                          {order.status === 'preparing' && (
                            <button
                              onClick={() => handleStatusChange(order.id, 'ready')}
                              className="flex-1 px-4 py-3 bg-emerald-500 text-white rounded-xl font-bold hover:bg-emerald-600 transition-all text-xs shadow-lg shadow-emerald-100"
                            >
                              {t("orders.markReady")}
                            </button>
                          )}
                          {order.status === 'ready' && (
                            <button
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowSettlement(true);
                              }}
                              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition-all text-xs shadow-lg shadow-purple-100"
                            >
                              {t("orders.closeBill")}
                            </button>
                          )}

                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0">
                            <button
                              onClick={() => navigate(`/muster-siparis-ekle/${order.id}`)}
                              className="p-3 bg-slate-100 dark:bg-[#141824] dark:border-[#2a2e3a] text-slate-600 rounded-xl hover:bg-blue-100 hover:text-blue-600 transition-all"
                              title={t("common.edit")}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteOrder(order.id)}
                              className="p-3 bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-100 transition-all"
                              title={t("common.delete")}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </>
                      )}

                      {activeTab === 'history' && (
                        <div className="w-full flex items-center justify-between">
                          <div className="flex flex-col">
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{t("orders.delivered")}</span>
                            <span className="text-xs font-semibold text-slate-600">{order.completedAt || t('orders.notSpecified')}</span>
                          </div>
                          <button
                            onClick={() => navigate(`/muster-siparis-ekle/${order.id}`)}
                            className="px-4 py-2 bg-slate-100 dark:bg-[#141824] dark:border-[#2a2e3a] text-slate-600 rounded-lg text-xs font-bold hover:bg-slate-200"
                          >
                            {t("orders.review")}
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {!loading && filteredOrders.length === 0 && (
          <div className="flex flex-col items-center justify-center h-96 text-slate-400">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] p-8 rounded-full mb-6 shadow-sm border border-slate-100"
            >
              <Package className="w-24 h-24 mb-0 opacity-20" />
            </motion.div>
            <p className="text-xl font-bold text-slate-600">{t("orders.empty")}</p>
            <p className="text-sm mt-2">{t("orders.emptyHint")}</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showSelectCourier && (
        <SelectCourierModal
          updateCustomerInfo={async (o, cId) => {
            window.location.reload();
          }}
          DataItemOrder={selectedOrder}
          couriers={[]}
          onSelect={(c) => {
            setShowSelectCourier(false);
          }}
          onClose={() => setShowSelectCourier(false)}
        />
      )}

      {showAddOrder && (
        <AddOrderModal
          onClose={() => setShowAddOrder(false)}
          onSuccess={() => {
            setShowAddOrder(false);
            loadOrders();
          }}
        />
      )}

      {showSettlement && selectedOrder && (
        <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center p-4 z-[999] backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-2xl bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] rounded-3xl shadow-2xl overflow-hidden relative border border-slate-100"
          >
            <div className="bg-slate-50 dark:bg-[#12141c] dark:border-[#2a2e3a] p-6 flex justify-between items-center border-b border-slate-100">
              <h3 className="text-xl font-bold text-slate-800">{t("orders.billSummary", { name: selectedOrder.customerName })}</h3>
              <button
                onClick={() => setShowSettlement(false)}
                className="w-10 h-10 flex items-center justify-center rounded-xl bg-white dark:bg-[#1a1d27] dark:text-gray-100 dark:border-[#2a2e3a] text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:bg-[#141824] dark:border-[#2a2e3a] transition-all font-bold text-2xl"
              >
                &times;
              </button>
            </div>
            <div className="p-8 max-h-[80vh] overflow-y-auto">
              <HesapKesSip
                orderId={selectedOrder.id}
                totalAmount={selectedOrder.total - selectedOrder.prepayment}
                orderStocks={[]}
                quickOrderId={selectedOrder.id}
              />
            </div>
          </motion.div>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}


